import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';
import { FiltersDropdown } from '@shared/components/ui/FiltersDropdown';
import { Search } from '@shared/components/ui/Search';
import { ColumnSelector } from '@shared/components/ui/ColumnSelector';
import { ColumnDef } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DownloadExcelButton } from './DownloadExcelButton';
import { OrdersShippedFiltersPanel } from './OrdersShippedFiltersPanel';

interface FiltersSectionProps {
  handleSearch: (searchFilter: { filters: any[]; logic: string }) => void;
  onFilterChange?: (conditions: FilterConditionOutput[]) => void;
  columns: ColumnDef<any>[];
  selectedColumns: string[];
  onColumnsChange: (newSelected: string[]) => void;
  filterConditions: FilterConditionOutput[];
  searchFilter?: { filters: any[]; logic: string };
  statusId?: number;
  shipmentStatusId?: number;
  sorting?: { field: string; direction: 'asc' | 'desc' };
}

export const FiltersSection = ({
  handleSearch,
  onFilterChange,
  columns,
  selectedColumns,
  onColumnsChange,
  filterConditions,
  searchFilter,
  statusId,
  shipmentStatusId,
  sorting,
}: FiltersSectionProps) => {
  const { t } = useTranslation('ordersShippedModule');
  const [inputValue, setInputValue] = useState('');

  // Sincronizar input con searchFilter (ref)
  useEffect(() => {
    const refFilter = searchFilter?.filters?.find((f: any) => f.column === 'ref' && f.operator === 'contains');
    const value = refFilter?.value ?? '';
    setInputValue((prev) => (prev === value ? prev : value));
  }, [searchFilter]);

  const activeFiltersCount = useMemo(() => filterConditions.length, [filterConditions]);

  const handleFilterChange = useCallback(
    (conditions: FilterConditionOutput[]) => {
      onFilterChange?.(conditions);
    },
    [onFilterChange],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleSearchSubmit = useCallback(() => {
    if (inputValue.trim() === '') {
      handleSearch({ filters: [], logic: 'AND' });
    } else {
      handleSearch({
        filters: [{ column: 'ref', operator: 'contains', value: inputValue.trim() }],
        logic: 'AND',
      });
    }
  }, [inputValue, handleSearch]);

  return (
    <>
      <Search
        placeholder={t('search.placeholder')}
        value={inputValue}
        onChange={handleInputChange}
        onEnter={handleSearchSubmit}
        dataTestId="search"
        inputDataTestId="search-input"
      />
      <FiltersDropdown
        title={t('filters.title', 'Filtros')}
        activeFiltersCount={activeFiltersCount}
        triggerTestId="filters-toggle-button"
      >
        <OrdersShippedFiltersPanel initialConditions={filterConditions} onChange={handleFilterChange} />
      </FiltersDropdown>
      <ColumnSelector
        columns={columns}
        selected={selectedColumns}
        onColumnsChange={onColumnsChange}
        fixedColumns={['ref']}
        storageKey="orders-shipped-table"
      />
      <DownloadExcelButton
        filterConditions={filterConditions}
        searchFilter={searchFilter}
        statusId={statusId}
        shipmentStatusId={shipmentStatusId}
        sorting={sorting}
        visibleColumns={selectedColumns}
      />
    </>
  );
};
